import { useState, useEffect } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Card, CardContent } from '@/components/ui/card'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Users, Shield, Building2, Trash2, Plus, Loader2 } from 'lucide-react'

/**
 * Types for access management
 */
interface AccessRule {
  id: string
  presentationId: string
  role: string | null
  userId: string | null
  companyId: string | null
  grantedBy: string | null
  notes: string | null
  isActive: boolean
  createdAt: string
  updatedAt: string
}

interface User {
  id: string
  name: string
  email: string
}

interface Company {
  id: string
  name: string
}

interface AccessManagementModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  presentationId: string
  presentationTitle: string
  onSuccess?: () => void
}

export default function AccessManagementModal({
  open,
  onOpenChange,
  presentationId,
  presentationTitle,
  onSuccess,
}: AccessManagementModalProps) {
  // Local state
  const [accessRules, setAccessRules] = useState<AccessRule[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [users, setUsers] = useState<User[]>([])
  const [companies, setCompanies] = useState<Company[]>([])

  // Form state for granting access
  const [accessType, setAccessType] = useState<'role' | 'user' | 'company'>('role')
  const [selectedRole, setSelectedRole] = useState<string>('')
  const [selectedUserId, setSelectedUserId] = useState<string>('')
  const [selectedCompanyId, setSelectedCompanyId] = useState<string>('')
  const [notes, setNotes] = useState<string>('')
  const [grantingAccess, setGrantingAccess] = useState(false)

  // Fetch access rules and reference data when modal opens
  useEffect(() => {
    if (open) {
      fetchAccessRules()
      fetchUsers()
      fetchCompanies()
    }
  }, [open, presentationId])

  const fetchAccessRules = async () => {
    try {
      setLoading(true)
      setError(null)

      const response = await fetch(
        `http://localhost:3007/api/presentation-library/${presentationId}/access`,
        {
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      )

      if (!response.ok) {
        throw new Error(`Failed to fetch access rules: ${response.statusText}`)
      }

      const data = await response.json()

      if (data.success && data.data) {
        setAccessRules(data.data)
      } else {
        throw new Error(data.error?.message || 'Failed to fetch access rules')
      }
    } catch (err) {
      console.error('Error fetching access rules:', err)
      setError(err instanceof Error ? err.message : 'Failed to load access rules')
    } finally {
      setLoading(false)
    }
  }

  const fetchUsers = async () => {
    try {
      const response = await fetch('http://localhost:3007/api/users', {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      })

      if (response.ok) {
        const data = await response.json()
        if (data.success && data.data) {
          setUsers(data.data)
        }
      }
    } catch (err) {
      console.error('Error fetching users:', err)
    }
  }

  const fetchCompanies = async () => {
    try {
      const response = await fetch('http://localhost:3007/api/companies', {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      })

      if (response.ok) {
        const data = await response.json()
        if (data.success && data.data) {
          setCompanies(data.data)
        }
      }
    } catch (err) {
      console.error('Error fetching companies:', err)
    }
  }

  const handleGrantAccess = async () => {
    // Validate form
    if (accessType === 'role' && !selectedRole) {
      setError('Please select a role')
      return
    }
    if (accessType === 'user' && !selectedUserId) {
      setError('Please select a user')
      return
    }
    if (accessType === 'company' && !selectedCompanyId) {
      setError('Please select a company')
      return
    }

    try {
      setGrantingAccess(true)
      setError(null)

      const payload: any = {}

      if (accessType === 'role') {
        payload.role = selectedRole
      } else if (accessType === 'user') {
        payload.userId = selectedUserId
      } else if (accessType === 'company') {
        payload.companyId = selectedCompanyId
      }

      if (notes) {
        payload.notes = notes
      }

      const response = await fetch(
        `http://localhost:3007/api/presentation-library/${presentationId}/access`,
        {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        }
      )

      if (!response.ok) {
        throw new Error(`Failed to grant access: ${response.statusText}`)
      }

      const data = await response.json()

      if (data.success) {
        // Reset form
        setSelectedRole('')
        setSelectedUserId('')
        setSelectedCompanyId('')
        setNotes('')

        // Refresh access rules
        await fetchAccessRules()

        // Call success callback
        if (onSuccess) {
          onSuccess()
        }
      } else {
        throw new Error(data.error?.message || 'Failed to grant access')
      }
    } catch (err) {
      console.error('Error granting access:', err)
      setError(err instanceof Error ? err.message : 'Failed to grant access')
    } finally {
      setGrantingAccess(false)
    }
  }

  const handleRevokeAccess = async (accessId: string) => {
    if (!confirm('Are you sure you want to revoke this access? This action cannot be undone.')) {
      return
    }

    try {
      setError(null)

      const response = await fetch(
        `http://localhost:3007/api/presentation-library/${presentationId}/access/${accessId}`,
        {
          method: 'DELETE',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      )

      if (!response.ok) {
        throw new Error(`Failed to revoke access: ${response.statusText}`)
      }

      const data = await response.json()

      if (data.success) {
        // Refresh access rules
        await fetchAccessRules()

        // Call success callback
        if (onSuccess) {
          onSuccess()
        }
      } else {
        throw new Error(data.error?.message || 'Failed to revoke access')
      }
    } catch (err) {
      console.error('Error revoking access:', err)
      setError(err instanceof Error ? err.message : 'Failed to revoke access')
    }
  }

  // Filter access rules by type
  const roleAccess = accessRules.filter((rule) => rule.role !== null)
  const userAccess = accessRules.filter((rule) => rule.userId !== null)
  const companyAccess = accessRules.filter((rule) => rule.companyId !== null)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Manage Access - {presentationTitle}</DialogTitle>
          <DialogDescription>
            Control who can view this presentation by granting access to roles, specific users, or
            companies.
          </DialogDescription>
        </DialogHeader>

        {error && (
          <div className="bg-destructive/10 border border-destructive/50 rounded-lg p-4">
            <div className="flex">
              <svg
                className="w-5 h-5 text-destructive flex-shrink-0"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
              <div className="ml-3">
                <p className="text-sm text-destructive">{error}</p>
              </div>
              <button
                onClick={() => setError(null)}
                className="ml-auto text-sm text-destructive hover:underline"
              >
                Dismiss
              </button>
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
            <span className="ml-3 text-muted-foreground">Loading access rules...</span>
          </div>
        ) : (
          <Tabs defaultValue="current" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="current">Current Access</TabsTrigger>
              <TabsTrigger value="grant">Grant New Access</TabsTrigger>
            </TabsList>

            {/* Current Access Tab */}
            <TabsContent value="current" className="space-y-4">
              {accessRules.length === 0 ? (
                <Card>
                  <CardContent className="flex flex-col items-center justify-center py-12">
                    <Shield className="w-12 h-12 text-muted-foreground mb-3" />
                    <p className="text-muted-foreground">No access rules defined yet</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      Grant access to roles, users, or companies to get started
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <>
                  {/* Role-based Access */}
                  {roleAccess.length > 0 && (
                    <Card>
                      <CardContent className="p-4">
                        <div className="flex items-center gap-2 mb-3">
                          <Users className="w-5 h-5 text-blue-500" />
                          <h3 className="font-semibold">Role-based Access</h3>
                        </div>
                        <div className="space-y-2">
                          {roleAccess.map((rule) => (
                            <div
                              key={rule.id}
                              className="flex items-center justify-between p-3 bg-muted rounded-lg"
                            >
                              <div className="flex items-center gap-3">
                                <Badge variant="secondary" className="capitalize">
                                  {rule.role}
                                </Badge>
                                {rule.notes && (
                                  <span className="text-sm text-muted-foreground">
                                    {rule.notes}
                                  </span>
                                )}
                              </div>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleRevokeAccess(rule.id)}
                                className="text-destructive hover:text-destructive"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* User-specific Access */}
                  {userAccess.length > 0 && (
                    <Card>
                      <CardContent className="p-4">
                        <div className="flex items-center gap-2 mb-3">
                          <Shield className="w-5 h-5 text-green-500" />
                          <h3 className="font-semibold">User-specific Access</h3>
                        </div>
                        <div className="space-y-2">
                          {userAccess.map((rule) => {
                            const user = users.find((u) => u.id === rule.userId)
                            return (
                              <div
                                key={rule.id}
                                className="flex items-center justify-between p-3 bg-muted rounded-lg"
                              >
                                <div className="flex items-center gap-3">
                                  <div>
                                    <p className="font-medium">{user?.name || 'Unknown User'}</p>
                                    <p className="text-sm text-muted-foreground">{user?.email}</p>
                                  </div>
                                  {rule.notes && (
                                    <span className="text-sm text-muted-foreground">
                                      ({rule.notes})
                                    </span>
                                  )}
                                </div>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleRevokeAccess(rule.id)}
                                  className="text-destructive hover:text-destructive"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            )
                          })}
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Company-specific Access */}
                  {companyAccess.length > 0 && (
                    <Card>
                      <CardContent className="p-4">
                        <div className="flex items-center gap-2 mb-3">
                          <Building2 className="w-5 h-5 text-purple-500" />
                          <h3 className="font-semibold">Company-specific Access</h3>
                        </div>
                        <div className="space-y-2">
                          {companyAccess.map((rule) => {
                            const company = companies.find((c) => c.id === rule.companyId)
                            return (
                              <div
                                key={rule.id}
                                className="flex items-center justify-between p-3 bg-muted rounded-lg"
                              >
                                <div className="flex items-center gap-3">
                                  <span className="font-medium">{company?.name || 'Unknown Company'}</span>
                                  {rule.notes && (
                                    <span className="text-sm text-muted-foreground">
                                      ({rule.notes})
                                    </span>
                                  )}
                                </div>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleRevokeAccess(rule.id)}
                                  className="text-destructive hover:text-destructive"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            )
                          })}
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </>
              )}
            </TabsContent>

            {/* Grant New Access Tab */}
            <TabsContent value="grant" className="space-y-4">
              <Card>
                <CardContent className="p-6 space-y-4">
                  {/* Access Type Selection */}
                  <div className="space-y-2">
                    <Label>Access Type</Label>
                    <Select value={accessType} onValueChange={(v: any) => setAccessType(v)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select access type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="role">
                          <div className="flex items-center gap-2">
                            <Users className="w-4 h-4 text-blue-500" />
                            <span>Role-based Access</span>
                          </div>
                        </SelectItem>
                        <SelectItem value="user">
                          <div className="flex items-center gap-2">
                            <Shield className="w-4 h-4 text-green-500" />
                            <span>User-specific Access</span>
                          </div>
                        </SelectItem>
                        <SelectItem value="company">
                          <div className="flex items-center gap-2">
                            <Building2 className="w-4 h-4 text-purple-500" />
                            <span>Company-specific Access</span>
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Role Selection */}
                  {accessType === 'role' && (
                    <div className="space-y-2">
                      <Label>Role</Label>
                      <Select value={selectedRole} onValueChange={setSelectedRole}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select a role" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="demo">Demo User</SelectItem>
                          <SelectItem value="company_user">Company User</SelectItem>
                          <SelectItem value="company_admin">Company Admin</SelectItem>
                          <SelectItem value="super_admin">Super Admin</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {/* User Selection */}
                  {accessType === 'user' && (
                    <div className="space-y-2">
                      <Label>User</Label>
                      <Select value={selectedUserId} onValueChange={setSelectedUserId}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select a user" />
                        </SelectTrigger>
                        <SelectContent>
                          {users.map((user) => (
                            <SelectItem key={user.id} value={user.id}>
                              {user.name} ({user.email})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {/* Company Selection */}
                  {accessType === 'company' && (
                    <div className="space-y-2">
                      <Label>Company</Label>
                      <Select value={selectedCompanyId} onValueChange={setSelectedCompanyId}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select a company" />
                        </SelectTrigger>
                        <SelectContent>
                          {companies.map((company) => (
                            <SelectItem key={company.id} value={company.id}>
                              {company.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {/* Notes (Optional) */}
                  <div className="space-y-2">
                    <Label htmlFor="notes">Notes (Optional)</Label>
                    <Input
                      id="notes"
                      type="text"
                      placeholder="Reason for granting access..."
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                    />
                  </div>

                  {/* Grant Access Button */}
                  <Button
                    onClick={handleGrantAccess}
                    disabled={grantingAccess}
                    className="w-full"
                  >
                    {grantingAccess ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Granting Access...
                      </>
                    ) : (
                      <>
                        <Plus className="w-4 h-4 mr-2" />
                        Grant Access
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
